import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, Check, Eye, Loader2, RefreshCw, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";

export const ManuscriptAnalysisModal = ({ open, onOpenChange, project, projectId, API }) => {
  const [tab, setTab] = useState("readability");
  const [readability, setReadability] = useState(null);
  const [consistency, setConsistency] = useState(null);
  const [running, setRunning] = useState(false);

  const runAnalysis = async () => {
    setRunning(true);
    try {
      const [consRes, readRes] = await Promise.all([
        axios.post(`${API}/analyze/consistency/${projectId}`),
        axios.post(`${API}/analyze/readability/${projectId}`)
      ]);
      setConsistency(consRes.data);
      setReadability(readRes.data);
      toast.success("Analysis refreshed");
    } catch {
      toast.error("Analysis failed");
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (open && !readability && !consistency && !running) {
      runAnalysis();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-literary flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Manuscript Analysis Dashboard
          </DialogTitle>
          <DialogDescription>
            Hemingway-style readability metrics, consistency checks, and pacing analysis — no AI cost.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          <div className="flex gap-2 mb-4 border-b border-slate-700 pb-2">
            {[
              { id: "readability", label: "Readability", icon: Eye },
              { id: "consistency", label: "Consistency", icon: Check },
              { id: "pacing", label: "Pacing", icon: Zap }
            ].map(t => (
              <Button key={t.id} variant={tab === t.id ? "default" : "ghost"} size="sm"
                onClick={() => setTab(t.id)} className={tab === t.id ? "bg-emerald-600" : ""}>
                <t.icon className="w-4 h-4 mr-2" />{t.label}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="ml-auto" onClick={runAnalysis} disabled={running}>
              <RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
          
          {running ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mb-4" />
              <p className="text-muted-foreground">Analyzing your manuscript...</p>
            </div>
          ) : (
            <>
              {tab === "readability" && readability && (
                <div className="space-y-4">
                  <div className={`rounded-xl p-4 ${readability.overall_rating === "excellent" ? "bg-emerald-500/20 border border-emerald-500/50" : readability.overall_rating === "good" ? "bg-cyan-500/20 border border-yellow-500/50" : "bg-red-500/20 border border-red-500/50"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-lg">{readability.overall_rating === "excellent" ? "Excellent Prose!" : readability.overall_rating === "good" ? "Good Overall" : "Needs Attention"}</h3>
                      <span className="text-2xl font-bold">{readability.readability_scores?.flesch_reading_ease || 0}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{readability.summary}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{readability.readability_scores?.flesch_reading_ease || 0}</p>
                      <p className="text-xs text-muted-foreground">Reading Ease</p>
                      <p className="text-xs text-emerald-400">{readability.readability_scores?.ease_label || ""}</p>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-400">Grade {readability.readability_scores?.flesch_kincaid_grade || 0}</p>
                      <p className="text-xs text-muted-foreground">Reading Level</p>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-cyan-400">{readability.passive_voice?.passive_percentage || 0}%</p>
                      <p className="text-xs text-muted-foreground">Passive Voice</p>
                      <p className="text-xs text-cyan-400">{readability.passive_voice?.rating || ""}</p>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-400">{readability.adverbs?.adverb_density || 0}%</p>
                      <p className="text-xs text-muted-foreground">Adverb Density</p>
                      <p className="text-xs text-purple-400">{readability.adverbs?.rating || ""}</p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h4 className="font-medium mb-3">Sentence Length Distribution</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {["short", "medium", "long", "very_long"].map(type => (
                        <div key={type} className="text-center">
                          <div className="h-20 bg-slate-700 rounded relative overflow-hidden">
                            <div className={`absolute bottom-0 left-0 right-0 ${type === "short" ? "bg-emerald-500" : type === "medium" ? "bg-blue-500" : type === "long" ? "bg-cyan-500" : "bg-red-500"}`}
                              style={{ height: `${readability.sentence_length?.distribution?.[type]?.percentage || 0}%` }} />
                          </div>
                          <p className="text-xs mt-1 capitalize">{type.replace("_", " ")}</p>
                          <p className="text-xs text-muted-foreground">{readability.sentence_length?.distribution?.[type]?.count || 0}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-center text-muted-foreground mt-2">Average: {readability.sentence_length?.average_length || 0} words/sentence</p>
                  </div>
                  
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h4 className="font-medium mb-3">Dialogue vs Narrative</h4>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-6 bg-slate-700 rounded-full overflow-hidden flex">
                        <div className="bg-cyan-500 h-full" style={{ width: `${readability.dialogue_ratio?.dialogue_percentage || 0}%` }} />
                        <div className="bg-violet-500 h-full" style={{ width: `${readability.dialogue_ratio?.narrative_percentage || 0}%` }} />
                      </div>
                    </div>
                    <div className="flex justify-between text-xs mt-2">
                      <span className="text-cyan-400">Dialogue: {readability.dialogue_ratio?.dialogue_percentage || 0}%</span>
                      <span className="text-violet-400">Narrative: {readability.dialogue_ratio?.narrative_percentage || 0}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{readability.dialogue_ratio?.assessment || ""}</p>
                  </div>
                  
                  {readability.adverbs?.top_adverbs?.length > 0 && (
                    <div className="bg-slate-800 rounded-lg p-4">
                      <h4 className="font-medium mb-3">Top Adverbs to Review</h4>
                      <div className="flex flex-wrap gap-2">
                        {readability.adverbs.top_adverbs.slice(0, 8).map((a, i) => (
                          <span key={i} className="px-2 py-1 bg-purple-500/20 border border-purple-500/50 rounded text-sm">
                            {a.word} <span className="text-purple-400">({a.count}x)</span>
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Tip: Replace adverbs with stronger verbs for tighter prose.</p>
                    </div>
                  )}
                </div>
              )}
              
              {tab === "readability" && !readability && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">Click "Refresh" to analyze your manuscript</p>
                  <Button onClick={runAnalysis} className="bg-emerald-600 hover:bg-emerald-700">Run Analysis</Button>
                </div>
              )}
              
              {tab === "consistency" && consistency && (
                <div className="space-y-4">
                  <div className={`rounded-xl p-4 ${consistency.total_issues === 0 ? "bg-emerald-500/20 border border-emerald-500/50" : consistency.total_issues < 5 ? "bg-cyan-500/20 border border-yellow-500/50" : "bg-red-500/20 border border-red-500/50"}`}>
                    <h3 className="font-semibold text-lg mb-1">{consistency.total_issues === 0 ? "Perfect Consistency!" : `Found ${consistency.total_issues} Issues`}</h3>
                    <p className="text-sm text-muted-foreground">{consistency.summary}</p>
                  </div>
                  {consistency.issues?.length > 0 && (
                    <div className="space-y-3">
                      {consistency.issues.map((issue, i) => (
                        <div key={i} className={`rounded-lg p-3 ${issue.severity === "high" ? "bg-red-500/20 border border-red-500/50" : issue.severity === "medium" ? "bg-cyan-500/20 border border-cyan-500/30" : "bg-slate-700"}`}>
                          <span className={`text-xs px-2 py-0.5 rounded ${issue.severity === "high" ? "bg-red-600" : issue.severity === "medium" ? "bg-cyan-600" : "bg-slate-600"}`}>{issue.type.replace(/_/g, " ")}</span>
                          <p className="mt-1 text-sm">{issue.message}</p>
                          {issue.suggestion && <p className="text-xs text-muted-foreground mt-1">{issue.suggestion}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                  {consistency.total_issues === 0 && (
                    <div className="text-center py-8">
                      <Check className="w-12 h-12 text-emerald-500 mb-3" />
                      <p className="text-lg font-medium">Your manuscript is consistent!</p>
                    </div>
                  )}
                </div>
              )}
              
              {tab === "consistency" && !consistency && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">Click "Refresh" to check consistency</p>
                  <Button onClick={runAnalysis} className="bg-emerald-600 hover:bg-emerald-700">Run Analysis</Button>
                </div>
              )}
              
              {tab === "pacing" && (
                <div className="space-y-4">
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h4 className="font-medium mb-3">Chapter Length Distribution</h4>
                    {project?.chapter_outline?.length > 0 ? (
                      <div className="space-y-2">
                        {project.chapter_outline.map((ch, i) => {
                          const wordCount = ch.word_count || (ch.summary?.split(" ")?.length || 0) * 50;
                          const maxWords = Math.max(...project.chapter_outline.map(c => c.word_count || 2000));
                          const percentage = (wordCount / maxWords) * 100;
                          return (
                            <div key={i} className="flex items-center gap-2">
                              <span className="text-xs w-16 truncate">Ch {ch.number || i + 1}</span>
                              <div className="flex-1 h-4 bg-slate-700 rounded overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500" style={{ width: `${percentage}%` }} />
                              </div>
                              <span className="text-xs w-12 text-right text-muted-foreground">{wordCount?.toLocaleString() || "~"}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">Generate chapters to see pacing analysis</p>
                    )}
                  </div>
                  <div className="bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Pacing Tips</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>- <strong>Consistent chapters:</strong> Aim for chapters within 20% of your average length</li>
                      <li>- <strong>Short chapters:</strong> Create urgency at climactic moments</li>
                      <li>- <strong>Longer chapters:</strong> Use for complex emotional or plot developments</li>
                      <li>- <strong>Variety is good:</strong> Mix lengths to control reading rhythm</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
